/**
 * Multi-company app-bar scope helpers (WI-1: global CompanyScopeSwitcher).
 *
 * The global company *scope* is a single per-user filter surfaced in the app
 * bar — distinct from the on-record company (`DocumentCompanyField`). See
 * `docs/platform/multi-company-design.md` §"Global company scope (app bar)".
 *
 * Pure, React-free helpers so both the axios interceptor (non-React) and React
 * components can share the same participation + surface logic.
 */
import { getModuleByPath } from '../config/modules.config';
import { isModuleCompanyScoped } from './companyScopingStore';

/**
 * True when the given module id participates in the multi-company dimension for
 * the current tenant.
 *
 * Participation is per-tenant and dynamic (WI-4): the ERP cluster + people are
 * always on, other opt-in modules are enabled per-module in their admin
 * settings. The live map is held in `companyScopingStore`; before it loads (and
 * for modules it omits) this falls back to the always-on ERP set, so the
 * pre-load behaviour matches the original hardcoded default.
 */
export function isCompanyScopedModuleId(moduleId: string): boolean {
  return isModuleCompanyScoped(moduleId);
}

/** True when the URL's module participates in the global app-bar scope. */
export function isCompanyScopedPath(pathname: string): boolean {
  try {
    return isCompanyScopedModuleId(getModuleByPath(pathname).id);
  } catch {
    return false;
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RECORD_VERB_SEGMENTS = new Set(['new', 'create', 'edit']);

/**
 * Heuristic for "is this a collection surface" (list / dashboard / report /
 * kanban) vs a single-record surface (detail / form).
 *
 * The app bar renders above the JSX `<Routes>` tree, so the matched page-type
 * is not available as route metadata — we infer it from the URL. A path is a
 * single-record surface when any segment is a record id (UUID) or the last
 * segment is a record verb (`new`/`create`/`edit`). Everything else is treated
 * as a collection surface.
 *
 * PanLuma uses UUID-only primary keys, so we deliberately do NOT treat bare
 * integer segments as record ids — those are literal route names (e.g. the
 * `1099` reporting center, `/accounting/reports/1099`), not detail pages.
 *
 * The scope value is never mutated by navigation regardless — this only governs
 * the control's *visibility* (the navigation invariant holds either way).
 */
export function isCollectionSurface(pathname: string): boolean {
  const segments = pathname.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (segments.length === 0) return true;

  const last = segments[segments.length - 1].toLowerCase();
  if (RECORD_VERB_SEGMENTS.has(last)) return false;

  return !segments.some((seg) => UUID_RE.test(seg));
}

/**
 * Should the global app-bar scope control be shown for this URL?
 * (Participating module AND a collection surface. The ≥2-company gate is
 * applied by the component, which has the companies query.)
 */
export function shouldShowCompanyScopeForPath(pathname: string): boolean {
  return isCompanyScopedPath(pathname) && isCollectionSurface(pathname);
}
